import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Button, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FlipCameraIosIcon from '@mui/icons-material/FlipCameraIos';

function StoreCamera() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [facingMode, setFacingMode] = useState('user'); // 'user' or 'environment'
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(null); // null means not started, then 5, 4, 3, 2, 1

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facingMode]);

  // Start countdown after camera is ready
  useEffect(() => {
    if (stream && !capturedImage && countdown === null) {
      // Wait 1 second after camera starts, then begin countdown
      const timer = setTimeout(() => {
        setCountdown(5);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [stream, capturedImage, countdown]);

  // Handle countdown and auto-capture
  useEffect(() => {
    if (countdown === null || countdown === 0 || capturedImage) return;

    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown, capturedImage]);

  // Auto-capture when countdown reaches 0
  useEffect(() => {
    if (countdown === 0 && !capturedImage) {
      capturePhoto();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown, capturedImage]);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      });

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        setStream(mediaStream);
        setError(null);
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Impossibile accedere alla fotocamera. Assicurati di aver dato i permessi.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = canvas.toDataURL('image/jpeg', 0.9);
      setCapturedImage(imageData);
      stopCamera();
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    setCountdown(null);
    startCamera();
  };

  const continueToResults = () => {
    // Salva l'immagine e naviga alla pagina contatti prima dell'armocromia
    localStorage.setItem('userPhoto', capturedImage);
    navigate('/contact?flow=armocromia');
  };

  const toggleCamera = () => {
    setFacingMode(facingMode === 'user' ? 'environment' : 'user');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#000000',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          padding: '16px',
          zIndex: 10,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <IconButton
          onClick={() => navigate('/store-quiz')}
          sx={{
            color: '#ffffff',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        {!capturedImage && (
          <IconButton
            onClick={toggleCamera}
            sx={{
              color: '#ffffff',
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }}
          >
            <FlipCameraIosIcon />
          </IconButton>
        )}
      </Box>

      {/* Video/Image container */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {error ? (
          <Box sx={{ textAlign: 'center', color: '#ffffff', p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/try-on')}
              sx={{ mt: 2 }}
            >
              Continua senza foto
            </Button>
          </Box>
        ) : capturedImage ? (
          <img
            src={capturedImage}
            alt="Captured"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain'
            }}
          />
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            {countdown !== null && countdown > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 20,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  borderRadius: '50%',
                  width: '120px',
                  height: '120px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography
                  sx={{
                    fontSize: '72px',
                    fontWeight: 700,
                    color: '#ffffff'
                  }}
                >
                  {countdown}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Hidden canvas for capturing */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Bottom controls */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '32px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 3
        }}
      >
        {capturedImage ? (
          <>
            <Button
              variant="outlined"
              onClick={retakePhoto}
              sx={{
                color: '#ffffff',
                borderColor: '#ffffff',
                padding: '12px 32px',
                fontSize: '16px',
                fontWeight: 500,
                '&:hover': {
                  borderColor: '#ffffff',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Riprova
            </Button>
            <Button
              variant="contained"
              onClick={continueToResults}
              sx={{
                backgroundColor: '#4CAF50',
                color: '#ffffff',
                padding: '12px 32px',
                fontSize: '16px',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: '#45a049'
                }
              }}
            >
              Conferma
            </Button>
          </>
        ) : null}
      </Box>
    </Box>
  );
}

export default StoreCamera;
